import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { pdfjs, Document, Page } from 'react-pdf';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import redirectLinks from 'constants/redirectLinks';
import { useLocation } from 'react-router-dom';
import { getUserAuthThunk } from '../../redux/slices/commonSlice';
import { useAppDispatch } from '../../redux/hooks';
import HttpConnection from '../../utils/httpConnection';
import { REST_API } from '../../constants/apiUrls';

// EOL対応: CSS for TextLayer need to be imported manually
// https://github.com/wojtekmaj/react-pdf/wiki/Upgrade-guide-from-version-5.x-to-6.x#textlayer-css-must-now-be-manually-imported
import 'react-pdf/dist/esm/Page/TextLayer.css';

// PDF.js worker設定
// https://github.com/wojtekmaj/react-pdf/releases/tag/v9.0.0
// PDF.js worker extension has been changed from .js to .mjs
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

// https://github.com/wojtekmaj/react-pdf/releases/tag/v9.0.0
// renderMode = svg has been removed, use canvas instead
// Since there was no alternative to svg, we need to optimize the rendering quality of canvas
// Set imageRendering: pixelated to canvas to make it look like svg
const DEFAULT_RENDER_MODE = 'canvas';

// EOL対応: canvasBackground need to be set manually
const DEFAULT_CANVAS_BACKGROUND = '#fff';

// CSS定義
const useStyles = makeStyles()(() => ({
  pdfDocument: {
    color: '#FFFFFF',
  },
  pdfPage: {
    margin: '10px 0 10px 0',
    backgroundColor: '#000 !important',
    '& canvas': {
      imageRendering: 'pixelated',
    },
  },
}));

/* ************ Type ************ */

/* ************ Component ************ */
const OperationExamplePdf: React.FC<React.PropsWithChildren<unknown>> = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { classes } = useStyles();
  const [file, setFile] = React.useState<string>('');
  const [numPages, setNumPages] = React.useState<number>();
  const [noData, setNoData] = React.useState<boolean>(false);
  const [render, setRender] = React.useState<string>(DEFAULT_RENDER_MODE);

  useEffect(() => {
    dispatch(getUserAuthThunk());
    getPdfUrl();
  }, []);

  // PDFファイル取得
  const getPdfUrl = async () => {
    const path = location.pathname.replace(redirectLinks.OPERATION_EXAMPLE_PDF, '').replace(/\//g, '');
    if (!path) {
      setNoData(true);
      return;
    }
    const http = new HttpConnection({ dispatch });
    let data = null;
    try {
      data = await http.getFile(`${REST_API.OPERATION_EXAMPLE.DOWNLOAD_PDF}/${path}`);
    } catch (error) {
      setNoData(true);
      return;
    }
    const pdfUrl = window.URL.createObjectURL(new Blob([data]));
    setFile(pdfUrl);
  };

  function onDocumentLoadSuccess({ numPages: numPageAll }: PDFDocumentProxy): void {
    setNumPages(numPageAll);
  }
  function onRenderError(): void {
    setRender(DEFAULT_RENDER_MODE);
  }

  return (
    <Box className="detail">
      <div>
        <Document
          file={file}
          onLoadSuccess={onDocumentLoadSuccess}
          className={classes.pdfDocument}
          noData=""
          renderMode={DEFAULT_RENDER_MODE}>
          {Array.from(new Array(numPages), (el, index) => (
            <Page
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              className={classes.pdfPage}
              width={1280}
              canvasBackground={DEFAULT_CANVAS_BACKGROUND}
              renderAnnotationLayer={false}
              renderMode={render}
              onRenderError={onRenderError}
            />
          ))}
        </Document>
      </div>
      <Box className={classes.pdfDocument} style={{ visibility: noData ? 'visible' : 'hidden' }}>
        No PDF file.
      </Box>
    </Box>
  );
};

export default OperationExamplePdf;
